import { useCallback } from "react";
import { getPlannedList } from "../../../api/Materials";
import {
  BinData,
  PlannedListItem,
} from "../../../models/materialissue/MaterialIssue";
import { ItemListEntry } from "../../../pages/MaterialIssue";
import { getBinsOfItemStoreLocation } from "../../../api/ItemLocationBins";
import { useWorkOrderStore } from "../../../store/wo-order";

type Payload = {
  requisition?: string;
  storesLocation: string;
  workOrder: string;
  isIssueMaterial: boolean;
};

export default function useGetPlannedListItems() {
  const setWoIssueItems = useWorkOrderStore((state) => state.setWoIssueItems);
  const setWoReturnItems = useWorkOrderStore((state) => state.setWoReturnItems);

  const getDefaultTransactionQuantity = (
    item: PlannedListItem,
    binData: BinData[]
  ) => {
    const binDataWithQuantity = binData.map((bin) => {
      if (bin.Bin === item.Bin) {
        return { ...bin, TransactionQuantity: Math.max(0, item.LineQuantityRequested - item.LineQuantityIssued) };
      }
      return bin;
    });
    return binDataWithQuantity;
  };

  const getBinData = async (
    item: string,
    storesLocation: string
  ): Promise<BinData[]> => {
    if (!item || !storesLocation) {
      return [];
    }
    const body = {
      item,
      storesLocation,
    };
    const response = await getBinsOfItemStoreLocation(body);
    return response.data ?? [];
  };

  const attachBinData = useCallback(
    async (
      data: PlannedListItem[],
      isAdhoc: boolean,
      isIssueMaterial: boolean
    ) => {
      const items: ItemListEntry[] = [];
      for (const item of data) {
        const binData = await getBinData(item.Item, item.StoresLocation);
        const binDataWithQuantity = isIssueMaterial
          ? getDefaultTransactionQuantity(item, binData)
          : binData;

        items.push({
          item: item,
          binData: binDataWithQuantity,
          checked: false,
          issued: false,
          isAdhoc,
          comments: "",
        });
      }
      return items;
    },
    []
  );

  const getPlannedListItems = useCallback(
    async (payload: Payload) => {
      const { isIssueMaterial, storesLocation, workOrder, requisition } =
        payload;
      const body = {
        requisition,
        storesLocation,
        workOrder,
      };
      const response = await getPlannedList(body);
      const items = await attachBinData(
        response.data?.MaterialList ?? [],
        false,
        isIssueMaterial
      );
      isIssueMaterial
        ? setWoIssueItems(workOrder, items)
        : setWoReturnItems(workOrder, items);

      return { response, items };
    },
    [attachBinData, setWoIssueItems, setWoReturnItems]
  );

  return { getPlannedListItems };
}
